/**
 * Security headers for every response this site sends.
 *
 * Applied in two places from this one definition, so they cannot disagree:
 *
 *   - the Worker (src/worker/security.ts) sets them on everything it answers,
 *     which is nearly every request (run_worker_first in wrangler.jsonc), and
 *   - scripts/prerender.mjs writes the `site` set into dist/_headers for the
 *     few paths Static Assets answers without the Worker (hashed bundles and
 *     images).
 *
 * Besides nosniff and Referrer-Policy they set a Content-Security-Policy,
 * HSTS (production only), a Permissions-Policy and frame-ancestors 'none'.
 *
 * Three profiles:
 *
 *   site   The prerendered pages. Scripts, styles, images and fetches are
 *          same-origin only; nothing may frame the site. No inline script is
 *          needed: JSON-LD blocks are data, not script, and are not executed.
 *   admin  The self-hosted Sveltia CMS under /admin. It needs inline styles,
 *          blob: workers and previews, and talks to the GitHub API, so its
 *          policy is wider — but only on /admin.
 *   auth   The CMS OAuth popup pages under /auth. Their one inline script is
 *          allowed by a per-response nonce.
 */

/** Strict-Transport-Security for production: one year, every subdomain, preload-eligible. */
export const HSTS = 'max-age=31536000; includeSubDomains; preload';

export type SecurityProfile = 'site' | 'admin' | 'auth';

export interface SecurityHeaderOptions {
  /** True only for APP_ENV=production: adds HSTS and upgrade-insecure-requests. */
  production: boolean;
  /** Required for the `auth` profile: the nonce its inline script carries. */
  nonce?: string;
}

const PERMISSIONS_POLICY = [
  'accelerometer=()',
  'autoplay=()',
  'browsing-topics=()',
  'camera=()',
  'display-capture=()',
  'geolocation=()',
  'gyroscope=()',
  'magnetometer=()',
  'microphone=()',
  'payment=()',
  'usb=()',
].join(', ');

function csp(profile: SecurityProfile, { production, nonce }: SecurityHeaderOptions): string {
  const common = ["base-uri 'self'", "object-src 'none'", "frame-ancestors 'none'"];
  let directives: string[];
  switch (profile) {
    case 'site':
      directives = [
        "default-src 'self'",
        "script-src 'self'",
        "style-src 'self'",
        "img-src 'self' data:",
        "font-src 'self'",
        "connect-src 'self'",
        "manifest-src 'self'",
        "frame-src 'none'",
        "form-action 'self'",
      ];
      break;
    case 'admin':
      // Sveltia CMS loads a few optional modules (syntax highlighting, image
      // conversion) from unpkg when they are not bundled, and its UI fonts
      // from jsDelivr's fontsource. Those sources are allowed by path, on
      // /admin only. Derived from inspecting the bundle; see README "CMS".
      directives = [
        "default-src 'self'",
        "script-src 'self' blob: https://unpkg.com/@sveltia/ https://unpkg.com/@shikijs/",
        "style-src 'self' 'unsafe-inline' https://cdn.jsdelivr.net/fontsource/",
        "img-src 'self' data: blob: https:",
        "media-src 'self' data: blob: https:",
        "font-src 'self' data: https://cdn.jsdelivr.net/fontsource/",
        "connect-src 'self' blob: data: https://api.github.com https://github.com https://*.githubusercontent.com https://www.githubstatus.com https://unpkg.com/@sveltia/ https://unpkg.com/@shikijs/",
        "worker-src 'self' blob:",
        "frame-src 'self' blob:",
        "form-action 'self'",
      ];
      break;
    case 'auth':
      if (!nonce) throw new Error('The auth security profile needs a nonce');
      directives = [
        "default-src 'none'",
        `script-src 'nonce-${nonce}'`,
        "style-src 'none'",
        "img-src 'none'",
        "connect-src 'none'",
        "form-action 'none'",
      ];
      break;
  }
  if (production) directives.push('upgrade-insecure-requests');
  return [...directives, ...common].join('; ');
}

export function securityHeaders(profile: SecurityProfile, options: SecurityHeaderOptions): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Security-Policy': csp(profile, options),
    'X-Content-Type-Options': 'nosniff',
    'Referrer-Policy': 'strict-origin-when-cross-origin',
    'Permissions-Policy': PERMISSIONS_POLICY,
    // Legacy companion to frame-ancestors for browsers that predate it.
    'X-Frame-Options': 'DENY',
  };
  // Not on /admin or /auth: the CMS signs in through a popup that goes to
  // github.com and back, and a same-origin opener policy would cut the popup
  // off from the window waiting for its token.
  if (profile === 'site') headers['Cross-Origin-Opener-Policy'] = 'same-origin';
  // HSTS only where the host is known to be served over HTTPS for good
  // (production). includeSubDomains and preload were approved by the owner;
  // a preload-list submission itself is made for the apex domain.
  if (options.production) headers['Strict-Transport-Security'] = HSTS;
  return headers;
}

/** The profile a request path falls under. */
export function securityProfileFor(pathname: string): SecurityProfile {
  if (pathname === '/admin' || pathname.startsWith('/admin/')) return 'admin';
  if (pathname === '/auth' || pathname.startsWith('/auth/')) return 'auth';
  return 'site';
}
